/**
 * useEditSession — request an OnlyOffice edit session for a document.
 * Returns the signed Document-Server config the iframe needs.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface OnlyOfficeConfig {
  document: {
    fileType: string;
    key: string;
    title: string;
    url: string;
    permissions?: Record<string, boolean>;
  };
  documentType: 'word' | 'cell' | 'slide';
  editorConfig: {
    mode: 'edit' | 'view';
    callbackUrl?: string;
    lang?: string;
    user?: { id: string; name: string };
    customization?: Record<string, unknown>;
  };
  width?: string;
  height?: string;
  /** JWT signed by the server using the shared secret with Document Server. */
  token: string;
}

export interface EditSessionResponse {
  editSessionId: string;
  documentServerUrl: string;
  config: OnlyOfficeConfig;
  /** 'edit' for DRAFT / IN_REVIEW; 'view' for EFFECTIVE / WITHDRAWN. */
  editorMode?: 'edit' | 'view';
  /** Server-side document version status driving the editorMode. */
  versionStatus?: string | null;
  /** Human-readable explanation when editorMode === 'view'. */
  viewOnlyReason?: string | null;
}

export function useEditSession(documentId: string | undefined): UseQueryResult<EditSessionResponse, Error> {
  return useQuery({
    queryKey: ['edit-session', documentId],
    queryFn: ({ signal }) =>
      apiFetch<EditSessionResponse>(`/api/v1/documents/${encodeURIComponent(documentId!)}/edit-session`, { signal }),
    enabled: !!documentId,
    staleTime: 0,      // every entry to the editor screen issues a fresh challenge
    retry: false,
  });
}
